import React, { useState, useEffect } from "react";
import TextField2T from "../textfields/TextField2T";
import PrimaryButton from "../buttons/PrimaryButton";
import SecondaryButton from "../buttons/SecondaryButton";
import Space from "../layout/Space";
import SmallSpinner from "../loading/SmallSpinner";
import { queryCollection } from "../models/DataFunctions";

interface AddPartnerFicheModalProps {
  onClose: () => void;
  onSubmit: (partnerData: any) => void;
  loading: boolean;
  initialData?: any;
  isEdit?: boolean;
  orgId: string;
}

function AddPartnerFicheModal({
  onClose,
  onSubmit,
  loading,
  initialData,
  isEdit = false,
  orgId,
}: AddPartnerFicheModalProps) {
  const [entreprise, setEntreprise] = useState("");
  const [email, setEmail] = useState("");
  const [tel, setTel] = useState("");
  const [city, setCity] = useState("");
  const [adresse, setAdresse] = useState("");
  const [codePostal, setCodePostal] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (initialData) {
      setEntreprise(initialData.entreprise || "");
      setEmail(initialData.email || "");
      setTel(initialData.tel || "");
      setCity(initialData.city || "");
      setAdresse(initialData.adresse || "");
      setCodePostal(initialData.code_postal || "");
    }
  }, [initialData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage("");

    if (!entreprise.trim()) {
      setErrorMessage("Le nom de l'entreprise est requis");
      return;
    }

    // Check for duplicate
    // When creating: always check
    // When editing: only check if the name has changed
    const shouldCheckDuplicate = !isEdit || (isEdit && entreprise.trim() !== initialData?.entreprise);

    if (shouldCheckDuplicate) {
      try {
        const filters = [
          { field: "org_id", operator: "==", value: orgId },
          { field: "type", operator: "==", value: "bookshelf_partner" },
          { field: "entreprise", operator: "==", value: entreprise.trim() }
        ];
        const existingFiches = await queryCollection("logement_bookshelf", filters);

        if (existingFiches && existingFiches.length > 0) {
          setErrorMessage(`Une fiche avec le nom "${entreprise.trim()}" existe déjà`);
          return;
        }
      } catch (error) {
        console.error("Error checking for duplicates:", error);
        setErrorMessage("Erreur lors de la vérification des doublons");
        return;
      }
    }

    const partnerData = {
      entreprise: entreprise.trim(),
      email: email.trim(),
      tel: tel.trim(),
      city: city.trim(),
      adresse: adresse.trim(),
      code_postal: codePostal.trim(),
    };

    onSubmit(partnerData);
  };

  return (
    <div className="bg-white rounded-3xl p-6 w-full max-w-c500 mx-4">
      <div className="text-hx2 font-semibold mb-4">
        {isEdit ? "Modifier la fiche partenaire" : "Ajouter une fiche partenaire"}
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-3">
        {errorMessage && (
          <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded-lg">
            {errorMessage}
          </div>
        )}

        <TextField2T
          label="Nom de l'entreprise *"
          value={entreprise}
          onChange={(e) => {
            setEntreprise(e.target.value);
            setErrorMessage(""); // Clear error when user types
          }}
        />

        <TextField2T
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />

        <TextField2T
          label="Téléphone"
          value={tel}
          onChange={(e) => setTel(e.target.value)}
        />

        <TextField2T
          label="Adresse"
          value={adresse}
          onChange={(e) => setAdresse(e.target.value)}
        />

        <TextField2T
          label="Ville"
          value={city}
          onChange={(e) => setCity(e.target.value)}
        />

        <TextField2T
          label="Code Postal"
          value={codePostal}
          onChange={(e) => setCodePostal(e.target.value)}
        />

        <Space y={10} />

        <div className="flex flex-row gap-2">
          <SecondaryButton onClick={onClose} className="flex-1">
            Annuler
          </SecondaryButton>
          <PrimaryButton type="submit" disabled={loading} className="flex-1">
            {loading ? (
              <div className="flex flex-row items-center gap-2 justify-center">
                <SmallSpinner />
                {isEdit ? "Modification..." : "Ajout..."}
              </div>
            ) : (
              <div>{isEdit ? "Modifier" : "Ajouter"}</div>
            )}
          </PrimaryButton>
        </div>
      </form>
    </div>
  );
}

export default AddPartnerFicheModal;
